// Per-print-location canvas thumbnails for the Designer sidebar, extracted from
// Designer.tsx. Owns the locationThumbs state; the canvas ref + guides are
// passed in via deps. Verbatim logic move.
import { useState, useCallback } from 'react'
import type { RefObject, MutableRefObject } from 'react'
import { fabric } from 'fabric'

export interface UseLocationThumbsDeps {
  fabricRef: RefObject<fabric.Canvas | null>
  printGuideRef: MutableRefObject<fabric.Rect | null>
  printLabelRef: MutableRefObject<fabric.Text | null>
}

export function useLocationThumbs(deps: UseLocationThumbsDeps) {
  const { fabricRef, printGuideRef, printLabelRef } = deps
  const [locationThumbs, setLocationThumbs] = useState<Record<string, string>>({})

  // Build a small data-URL thumbnail of the current canvas (no guide rect).
  // Called on location switch + object:modified so the sidebar always shows
  // a fresh preview of what's been placed on each location.
  const captureLocationThumb = useCallback((locationId: string | null) => {
    if (!locationId) return
    const c = fabricRef.current; if (!c) return
    const guide = printGuideRef.current; const label = printLabelRef.current
    const hasMedia = c.getObjects().some((o) => o !== guide && o !== label)
    if (!hasMedia) {
      setLocationThumbs((prev) => {
        if (!prev[locationId]) return prev
        const next = { ...prev }; delete next[locationId]; return next
      })
      return
    }
    // Hide guides during snapshot so the thumb shows just the design.
    const gv = guide?.visible; const lv = label?.visible
    if (guide) guide.visible = false
    if (label) label.visible = false
    c.requestRenderAll()
    try {
      const dataUrl = c.toDataURL({ format: 'png', quality: 0.6, multiplier: 0.18 })
      setLocationThumbs((prev) => ({ ...prev, [locationId]: dataUrl }))
    } catch { /* ignore taint errors on cross-origin images */ }
    if (guide) guide.visible = gv ?? true
    if (label) label.visible = lv ?? true
    c.requestRenderAll()
  }, [])

  return { locationThumbs, setLocationThumbs, captureLocationThumb }
}
